import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import userapiservice from "../../api/userapiservice";
import { getRequest } from "../../api/http";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
  ZoomControl,
  Tooltip,
} from "react-leaflet";
import { Button } from "antd";
import "./MapComponent.scss";
import L from "leaflet";
import locateMe from "../../assets/images/locateMe.png";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import { setCountry, setCountryCode, setLang, setLat } from "../../Reducer/localStorageSlice";
import { useSelector, useDispatch } from "react-redux";
import LocationMarker from "./Map/LocationMarker";
import MyComponent from "./Map/Mycomponent";
import { getMarkerByIndex } from "./Map/getMarkerByIndex";
import {
  collectMarkerLatLngs,
  filterOrganizationsByBounds,
  isValidLatLng,
  toCoordinate,
} from "./Map/markerCoordinates";
import usePartnerName from "../../hooks/usePartnerName";

const readMapViewport = (map) => {
  const bounds = map.getBounds();
  const center = map.getCenter();
  return {
    south: bounds.getSouth(),
    north: bounds.getNorth(),
    west: bounds.getWest(),
    east: bounds.getEast(),
    center: { latitude: center.lat, longitude: center.lng },
    zoom: map.getZoom(),
  };
};

const ViewportReporter = ({ onViewportChange, suspendReportingRef }) => {
  const map = useMap();
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  const userAdjustedRef = useRef(false);

  const allowUserViewportReporting = useCallback(() => {
    if (suspendReportingRef) suspendReportingRef.current = false;
    userAdjustedRef.current = true;
  }, [suspendReportingRef]);

  const report = () => {
    // Locate recenters the map programmatically. Ignore that move so the
    // organisation list stays on the initial results until the user pans or zooms.
    if (suspendReportingRef?.current) return;
    if (!userAdjustedRef.current) return;
    onViewportChangeRef.current?.(readMapViewport(map));
  };

  useMapEvents({
    dragstart: allowUserViewportReporting,
    boxzoomstart: allowUserViewportReporting,
    zoomstart: (event) => {
      if (event.originalEvent) allowUserViewportReporting();
    },
    moveend: report,
  });

  useEffect(() => {
    const container = map.getContainer();
    const markUserAdjust = (event) => {
      if (event.type === "click" && !event.target?.closest?.(".leaflet-control-zoom")) {
        return;
      }
      if (event.type === "touchstart" && event.touches?.length < 2) return;
      if (
        event.type === "keydown" &&
        !["+", "-", "=", "_"].includes(event.key)
      ) {
        return;
      }
      allowUserViewportReporting();
    };
    container.addEventListener("wheel", markUserAdjust, { capture: true, passive: true });
    container.addEventListener("dblclick", markUserAdjust, true);
    container.addEventListener("click", markUserAdjust, true);
    container.addEventListener("touchstart", markUserAdjust, { capture: true, passive: true });
    container.addEventListener("keydown", markUserAdjust, true);
    return () => {
      container.removeEventListener("wheel", markUserAdjust, true);
      container.removeEventListener("dblclick", markUserAdjust, true);
      container.removeEventListener("click", markUserAdjust, true);
      container.removeEventListener("touchstart", markUserAdjust, true);
      container.removeEventListener("keydown", markUserAdjust, true);
    };
  }, [map, allowUserViewportReporting]);

  return null;
};

const FocusSelectedLocation = ({ target }) => {
  const map = useMap();
  const lastTokenRef = useRef(null);

  useEffect(() => {
    if (!target?.token || target.token === lastTokenRef.current) return;
    const latitude = toCoordinate(target.latitude);
    const longitude = toCoordinate(target.longitude);
    if (!isValidLatLng(latitude, longitude)) return;
    lastTokenRef.current = target.token;
    const zoom = Math.max(map.getZoom() || 0, 15);
    map.flyTo([latitude, longitude], zoom, { duration: 0.75 });
  }, [target, map]);

  return null;
};

const ResultsViewport = ({ markers, active, onFitted }) => {
  const map = useMap();
  const onFittedRef = useRef(onFitted);
  onFittedRef.current = onFitted;

  useEffect(() => {
    if (!active) return undefined;
    const points = collectMarkerLatLngs(markers);
    if (!points.length) {
      onFittedRef.current?.(null);
      return undefined;
    }
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (points.length === 1) {
        map.setView(points[0], 15);
      } else {
        map.fitBounds(points, { padding: [48, 48], maxZoom: 14 });
      }
      const finish = () => {
        if (!cancelled) onFittedRef.current?.(readMapViewport(map));
      };
      const viewUnchanged =
        map.getZoom() === zoom && map.getCenter().equals(center);
      if (viewUnchanged) {
        finish();
      } else {
        map.once("moveend", finish);
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [active, markers, map]);

  return null;
};

const MapSearchControl = ({
  countryCode,
  mapSearchQuery = "",
  onMapSearchQueryChange,
  onMapSearchCleared,
}) => {
  const map = useMap();
  const [, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const searchDelay = useRef(null);
  const notFoundTimer = useRef(null);
  const provider = useRef(null);
  const mapSearchQueryRef = useRef(mapSearchQuery);
  mapSearchQueryRef.current = mapSearchQuery;

  useEffect(() => {
    provider.current = new OpenStreetMapProvider({
      params: {
        countrycodes: countryCode,
      },
    });

    const searchControl = new GeoSearchControl({
      provider: provider.current,
      searchLabel: "Search here",
      style: "bar",
      showMarker: true,
      showPopup: false,
      autoClose: false,
      keepResult: true,
      maxSuggestions: 20,
      notFoundMessage: "Sorry address not found", // we will show manually

      marker: {
        icon: L.icon({
          iconSize: [25, 41],
          iconAnchor: [10, 41],
          popupAnchor: [2, -40],
          iconUrl: true
            ? "https://unpkg.com/leaflet@1.6/dist/images/marker-icon.png"
            : "",
        }),
      },
    });

    map.addControl(searchControl);

    const input = document.querySelector(".leaflet-control-geosearch input");
    const cancelBtn = document.querySelector(".leaflet-control-geosearch button");

    // Restore persisted search text after view toggles / control remounts.
    if (input && mapSearchQueryRef.current) {
      input.value = mapSearchQueryRef.current;
    }

    // ---- Replace cancel icon with loader ----
    const showLoader = () => {
      if (!cancelBtn) return;
      cancelBtn.innerHTML = `
        <span class="loader" 
        style="
          display:inline-block;
          width:16px;
          height:16px;
          border:2px solid #ccc;
          border-top:2px solid #333;
          border-radius:50%;
          animation:spin 0.8s linear infinite;">
        </span>`;
    };

    const showCancel = () => {
      if (!cancelBtn) return;
      cancelBtn.innerHTML = "×";
    };

    // -------- Handle User Typing ----------
    const handleInput = () => {
      const query = input.value.trim();

      // reset states
      setNotFound(false);

      if (query.length === 0) {
        setLoading(false);
        showCancel();
        onMapSearchQueryChange?.("");
        return;
      }

      setLoading(true);
      showLoader();

      clearTimeout(searchDelay.current);
      clearTimeout(notFoundTimer.current);

      // Debounce search 850ms
      searchDelay.current = setTimeout(async () => {
        try {
          const results = await provider.current.search({ query });

          setTimeout(() => {
            const elements = document.querySelectorAll(
              ".leaflet-control-geosearch .results > div"
            );

            elements.forEach((el, index) => {
              const result = results[index];
              if (result) {
                el.setAttribute("title", result.label);
              }
            });
          }, 100);

          setLoading(false);
          showCancel();

          if (results && results.length > 0) {
            // ❌ DO NOT auto-select
            // ❌ DO NOT set input.value
            // ❌ DO NOT move map

            setNotFound(false);
            return;
          }

          // No results → delay "not found"
          notFoundTimer.current = setTimeout(() => {
            setNotFound(true);
          }, 1200);

        } catch (err) {
          setLoading(false);
          showCancel();
          setNotFound(true);
        }
      }, 850);
    };

    // ---- User clicks a suggestion (ONLY time we zoom) ----
    const handleShowLocation = (event) => {
      const { label } = event.location;
      setLoading(false);
      setNotFound(false);
      showCancel();

      input.value = label; // update only on click
      onMapSearchQueryChange?.(label);
    };
    map.on("geosearch/showlocation", handleShowLocation);

    const handleClearSearch = () => {
      input.value = "";
      setNotFound(false);
      showCancel();
      onMapSearchCleared?.();
    };

    input?.addEventListener("input", handleInput);
    cancelBtn?.addEventListener("click", handleClearSearch);

    return () => {
      map.removeControl(searchControl);
      map.off("geosearch/showlocation", handleShowLocation);
      input?.removeEventListener("input", handleInput);
      cancelBtn?.removeEventListener("click", handleClearSearch);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, countryCode]);

  // Keep the visible input in sync if persisted query changes while control stays mounted.
  useEffect(() => {
    const input = document.querySelector(".leaflet-control-geosearch input");
    if (!input) return;
    if ((mapSearchQuery || "") !== input.value) {
      input.value = mapSearchQuery || "";
    }
  }, [mapSearchQuery]);

  // ---- Not Found Message directly under search bar ----
  useEffect(() => {
    const container = document.querySelector(".leaflet-control-geosearch");
    if (!container) return;

    let msgBox = container.querySelector(".not-found-msg");

    if (!msgBox) {
      msgBox = document.createElement("div");
      msgBox.className = "not-found-msg";
      msgBox.style.cssText = `
        color: red;
        font-size: 12px;
        margin-top: 4px;
        margin-left: 4px;
      `;
      container.appendChild(msgBox);
    }

    msgBox.innerText = notFound ? "Sorry, that address could not be found." : "";

  }, [notFound]);

  return null;
};

export default function MapComponent({
  orgDirectoryData,
  radiusValue,
  ChangeRadiusHandler,
  markerData,
  selectedOrg,
  activeMiniPopup,
  allowSetView = false,
  disableSetView,
  cancelClick,
  updateCancelClick,
  focusResults = false,
  viewportBounds = null,
  onViewportChange,
  onViewportFitted,
  onOrganisationListReset,
  mapSearchQuery = "",
  onMapSearchQueryChange,
  onMapSearchCleared,
  isMapVisible = true,
  focusRequest = null,
}) {
  const dispatch = useDispatch();
  const popupRef = useRef(null);
  const markerRefs = useRef({});
  const mapRef = useRef(null);

  const localData = useSelector((state) => state?.localStorage)
  const { 'partner-latitude': partnerLatitude, 'partner-longitude': partnerLongitude, countryCode, lat, lng } = localData
  const { formattedName: partner } = usePartnerName()
  const zoom = localStorage?.getItem('zoom')

  const [, setPosition] = useState();
  const [mapMarkers, setMapMarkers] = useState(markerData);
  const [radius, setRadius] = useState(2);
  const [flyTo, setFlyTo] = useState(false);
  const [outsideClick, setOutsideClick] = useState("");
  const [toggleLocation, setToggleLocation] = useState(false);
  const [location, setLocation] = useState(null);
  const [locateRequestId, setLocateRequestId] = useState(0);
  const locateSeqRef = useRef(0);
  const suspendViewportReportRef = useRef(false);
  const fixedCoordinates = [partnerLatitude, partnerLongitude];
  const [tooltipIndex, setToolTipIndex] = useState("");
  const [hideToolTip, setHideToolTip] = useState("");
  const tooltipRefs = useRef({});

  const wasFocusedRef = useRef(false);
  useEffect(() => {
    if (wasFocusedRef.current && !focusResults) {
      setOutsideClick(false);
    }
    wasFocusedRef.current = focusResults;
  }, [focusResults]);

  useEffect(() => {
    if (cancelClick) {
      showAndHideTooltips(true);
    }
  }, [cancelClick]);

console.log("Testing mapMarkers", mapMarkers)
  // const SearchComponent = () => {
  //   const map = useMap();
  //   useEffect(() => {
  //     const provider = new OpenStreetMapProvider();
  //     const searchControl = new GeoSearchControl({
  //       provider,
  //       searchLabel: "Search here",
  //       style: "bar", // Search bar style
  //       showMarker: false,
  //       showPopup: false,
  //       autoClose: false, // Keep the result open
  //       keepResult: true, // Keep the search input value
  //       notFoundMessage: "Sorry, that address could not be found.",

  //       marker: {
  //         icon: L.icon({
  //           iconSize: [25, 41],
  //           iconAnchor: [10, 41],
  //           popupAnchor: [2, -40],
  //           iconUrl: true
  //             ? "https://unpkg.com/leaflet@1.6/dist/images/marker-icon.png"
  //             : "",
  //         }),
  //       },
  //     });

  //     // Add search control to the map
  //     map.addControl(searchControl);

  //     // Select the input field and cancel button
  //     const searchInput = document.querySelector(
  //       ".leaflet-control-geosearch input"
  //     );
  //     const cancelButton = document.querySelector(
  //       ".leaflet-control-geosearch button"
  //     );

  //     // Reset map when input is cleared or cancel is clicked
  //     const resetMap = () => {
  //       if (searchInput.value === "") {
  //         // Reset map view
  //         map.setView(fixedCoordinates, 6);
  //       }
  //     };

  //     searchInput?.addEventListener("input", resetMap);
  //     cancelButton?.addEventListener("click", resetMap);

  //     map.on("geosearch/showlocation", (event) => {
  //       if (event.location && searchInput) {
  //         const { x, y, label, } = event.location;

  //         const lat = y;
  //         const lng = x;

  //         if (window.currentPopup) {
  //           map.removeLayer(window.currentPopup);
  //         }

  //         // window.currentPopup = L.popup({ closeOnClick: false })
  //         //   .setLatLng([lat, lng])
  //         //   .setContent(`<b>${label}</b>`)
  //         //   .openOn(map);

  //         // setTimeout(() => {
  //         //   if (window.currentPopup) {
  //         //     map.closePopup(window.currentPopup);
  //         //     window.currentPopup = null;
  //         //   }
  //         // }, 5000);

  //         map.on("click", () => {
  //           if (window.currentPopup) {
  //             map.closePopup(window.currentPopup);
  //             window.currentPopup = null;
  //           }
  //         });
  //         searchInput.value = event.location.label;
  //         // map.setView([y, x], 8)
  //         map.setView([y, x], 16);
  //         ;
  //       }
  //     });

  //     return () => {
  //       map.removeControl(searchControl);
  //       searchInput?.removeEventListener("input", resetMap);
  //       cancelButton?.removeEventListener("click", resetMap);
  //     };
  //   }, [map]);

  //   return null;
  // };

  const getCountryFromCoords = async (lat, lng) => {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await res.json();
    return {
      country: data?.address?.country || "",
      countryCode: data?.address?.country_code || ""
    };
  };

  useEffect(()=>{
    if(lat && lng && !countryCode){
      getCountryFromCoords(lat, lng).then((res)=>{
        dispatch(setCountry(res.country))
        dispatch(setCountryCode(res.countryCode))
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[lat, lng])

  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(async (pos) => {
  //     const { latitude, longitude } = pos.coords;
  //     setPosition([latitude, longitude]);
  //     console.log("Latitude:", latitude, "Longitude:", longitude);
  //     const country = await getCountryFromCoords(latitude, longitude);

  //     localStorage.setItem("country", country);
  //   });
  // }, [])

  useEffect(() => {
    const center = viewportBounds?.center;
    const latitude = toCoordinate(center?.latitude);
    const longitude = toCoordinate(center?.longitude);
    if (isValidLatLng(latitude, longitude)) {
      // Restore prior search/pan position after list/map view toggles remount the map.
      setLocation([latitude, longitude]);
      const savedZoom = Number(viewportBounds?.zoom);
      if (Number.isFinite(savedZoom)) {
        setRadius(savedZoom);
      }
      setFlyTo(false);
      return;
    }
    handleLocate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMapVisible || !location) return undefined;
    const map = mapRef.current;
    if (!map?.invalidateSize) return undefined;
    const frame = requestAnimationFrame(() => {
      map.invalidateSize();
    });
    return () => cancelAnimationFrame(frame);
  }, [isMapVisible, location]);



  useEffect(() => {

    if (zoom >= 10) {
      showAndHideTooltips(true)
      handleMouseOverAndOut();

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom])



  useEffect(() => {
    handleMouseOverAndOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMarkers]);

  const MapEventHandler = () => {
    const map = useMap();
    useMapEvents({
      click: (e) => {
        const popupEl = popupRef.current;
        if (popupEl && !popupEl.contains(e.originalEvent.target)) {
          selectedOrg({});
          setOutsideClick(true);
        } else if (popupEl === null) {
          selectedOrg({});
          setOutsideClick(true);
        }
        setHideToolTip("");
        if (map.getZoom() > 10) {
          showAndHideTooltips(true);
        }
      },
    });
    return null;
  };
  const getPartnerLocation = async () => {
    if (localStorage.getItem("lat") && localStorage.getItem("lng")) {
      const latitude = localStorage.getItem("lat");
      const longitude = localStorage.getItem("lng");
      setPosition([latitude, longitude]);

    } else {
      if(!partner){
        return
      }
      try {
        const response = await getRequest(
          userapiservice.getPartnerDetails(partner)
        );

        const latitude = response.latitude;
        const longitude = response.longitude;
        setPosition([latitude, longitude]);
        dispatch(setLang(longitude))
        dispatch(setLat(latitude))
      } catch (error) { }
    }
  };

  const showAndHideTooltips = (show = false) => {
    Object.values(markerRefs.current).forEach((markerRef) => {
      if (markerRef) {
        if (show) {
          markerRef?.openTooltip();
        } else {
          markerRef?.closeTooltip();
        }
      }
    });
  };

  const handleLocate = (e, { fromUserClick = false } = {}) => {
    // Prevent Leaflet map click from racing and setting outsideClick=true.
    e?.preventDefault?.();
    e?.stopPropagation?.();
    e?.nativeEvent?.stopImmediatePropagation?.();

    if (fromUserClick) {
      suspendViewportReportRef.current = true;
      onOrganisationListReset?.();
    }

    // if (toggleLocation) {
    if (false) {
      setLocation(fixedCoordinates); // Set to fixed location
      setFlyTo(false);
      showAndHideTooltips(false);
    } else {
      showAndHideTooltips(false);
      setOutsideClick(false);
      setFlyTo(true);
      // Keep radius in sync with locate zoom so later radius sync cannot pull zoom back out.
      setRadius(13);

      // Re-center immediately on last known location (works after zoom/pan).
      if (location) {
        setLocateRequestId((id) => id + 1);
      }

      if (!navigator.geolocation) {
        setLocation(fixedCoordinates);
        setLocateRequestId((id) => id + 1);
        setToggleLocation(!toggleLocation);
        return;
      }

      const requestId = ++locateSeqRef.current;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (requestId !== locateSeqRef.current) return; // ignore superseded responses
          setLocation([position.coords.latitude, position.coords.longitude]);
          setFlyTo(true);
          setLocateRequestId((id) => id + 1);
        },
        () => {
          if (requestId !== locateSeqRef.current) return; // ignore superseded responses
          console.log('Unable to fetch your location. Falling back to fixed coordinates.')
          setLocation(fixedCoordinates); // Fallback if location fetch fails
          setFlyTo(true);
          setLocateRequestId((id) => id + 1);
        },
        { enableHighAccuracy: false, maximumAge: 30000, timeout: 10000 }
      );
    }
    setToggleLocation(!toggleLocation); // Toggle the state
  };

  const ZoomValueHandler = (value) => {
    setRadius(value);
    ChangeRadiusHandler(value);
  };

  useEffect(() => {
    if (radiusValue !== radius) {
      setRadius(radiusValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusValue]);

  useEffect(() => {
    let tempMapMarkers = [];
    (markerData || []).forEach((element, elementIndex) => {
      const outputs = Array.isArray(element?.addressOutputs)
        ? element.addressOutputs
        : [];
      outputs.forEach((item, index) => {
        const latitude = toCoordinate(item?.latitude);
        const longitude = toCoordinate(item?.longitude);
        if (!isValidLatLng(latitude, longitude)) return;
        tempMapMarkers.push({
          ...item,
          id: element.id,
          isActual: true,
          index: elementIndex,
          name: element.name,
          latitude,
          longitude,
          addressOutputs: [
            {
              ...element.addressOutputs[index],
              latitude,
              longitude,
            },
          ],
          sectorsOutputs: element.sectorsOutputs,
          carouselLink: element.carouselLink,
          micrositeLogo: element.micrositeLogo,
          businessDescription: element.businessDescription,
          pinColor: "Pink",
          showTooltip: false,
        });
      });
    });
    if (orgDirectoryData && orgDirectoryData.length > 0) {
      orgDirectoryData.forEach((element, elementIndex) => {
        const latitude = toCoordinate(element?.latitude);
        const longitude = toCoordinate(element?.longitude);
        if (!isValidLatLng(latitude, longitude)) return;
        const {
          contact,
          email,
          category,
          name,
          phoneNumber,
          partnerId,
          website,
          address,
          id,
        } = element;
        tempMapMarkers.push({
          id,
          latitude,
          longitude,
          index: elementIndex,
          name,
          category,
          contact,
          address,
          phoneNumber,
          email,
          isActual: false,
          addressOutputs: [
            {
              latitude,
              longitude,
              id: id || partnerId,
            },
          ],
          sectorsOutputs: [],
          website,
          showTooltip: true,
        });
      });
    }
    setMapMarkers(tempMapMarkers);
  }, [markerData, orgDirectoryData]);

  const visibleMapMarkers = useMemo(
    () => filterOrganizationsByBounds(mapMarkers, viewportBounds),
    [mapMarkers, viewportBounds],
  );

  useEffect(() => {
    getPartnerLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  useEffect(() => {
    if (activeMiniPopup?.closePopup) {
      const map = mapRef?.current;
      if (map && typeof map?.closePopup === "function") {
        map?.closePopup();
      }
    }
  }, [activeMiniPopup]);

  const markerOpenPopup = () => {
    if (mapMarkers?.length > 0 && activeMiniPopup?.addressOutputs?.length) {
      const matchingMarker = mapMarkers?.findIndex((item) =>
        item?.addressOutputs?.find(
          (output) => output?.id === activeMiniPopup?.addressOutputs[0]?.id
        )
      );

      const activeMarkerKey = `${mapMarkers[matchingMarker]?.addressOutputs[0]?.latitude}-${mapMarkers[matchingMarker]?.addressOutputs[0]?.longitude}`;
      const activeMarker = markerRefs?.current[activeMarkerKey];

      if (activeMarker) {
        activeMarker?.openPopup();
      }
    }
  };

  // function SetViewOnMarker({ markers }) {
  //   const map = useMap();

  //   useEffect(() => {
  //     const latitude = mapMarkers[0]?.addressOutputs[0]?.latitude;
  //     const longitude = mapMarkers[0]?.addressOutputs[0]?.longitude;

  //     if (markers?.length === 1) {
  //       map.setView([latitude, longitude], localStorage.getItem("zoom") || 6);

  //     } else if (markerData?.length > 1) {
  //       map.setView(
  //         [partnerLatitude, partnerLongitude],
  //         localStorage.getItem("zoom") || 6
  //       );

  //     }
  //     disableSetView();
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  //   }, []);

  //   return null;
  // }

  useEffect(() => {
    if (cancelClick) {
      setHideToolTip("");
    }
  }, [cancelClick]);

  useEffect(() => {
    const element = document.querySelector(
      `.leaflet-pane.leaflet-map-pane .leaflet-pane.leaflet-tooltip-pane .leaflet-tooltip.parent.marker-tooltip-${tooltipIndex}`
    );
    if (element) {
      if (hideToolTip) {
        element.classList.add("test_pass");
        handleMouseOverAndOut()
      } else {
        element.classList.remove("test_pass");
        showAndHideTooltips(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideToolTip]);

  const handleMouseOverAndOut = () => {
    const zoom = localStorage.getItem("zoom");
    if (zoom >= 10) {
      showAndHideTooltips(true);
    } else {
      showAndHideTooltips(false);
    }
    return null;
  };

  return (
    <div className="map-container-wrapper">
      {/* {location && markerData?.length > 0 ? ( */}
      {location ? (
        <MapContainer
          id="map-container"
          className="map-container"
          ref={mapRef}
          center={location}
          zoom={13}
          scrollWheelZoom={true}
          zoomControl={false}
          ZoomValueHandler={ZoomValueHandler}
          radius={radius}
        >
          <MapSearchControl
            countryCode={countryCode}
            mapSearchQuery={mapSearchQuery}
            onMapSearchQueryChange={onMapSearchQueryChange}
            onMapSearchCleared={onMapSearchCleared}
          />
          <MyComponent
            ZoomValueHandler={ZoomValueHandler}
            radius={radius}
            showAndHideTooltips={showAndHideTooltips}
            setToolTipIndex={setToolTipIndex}
          />
          <TileLayer
            attribution=""
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ZoomControl className="zoom-control" position="topright" />
          <LocationMarker
            targetLocation={location}
            outsideClick={outsideClick}
            flyTo={flyTo}
            suppressFly={focusResults}
            locateRequestId={locateRequestId}
          />
          <ResultsViewport
            markers={mapMarkers}
            active={focusResults}
            onFitted={onViewportFitted}
          />
          <ViewportReporter
            onViewportChange={onViewportChange}
            suspendReportingRef={suspendViewportReportRef}
          />
          <FocusSelectedLocation target={focusRequest} />
          {typeof navigator !== "undefined" && navigator.geolocation && (
            <Button
              onClick={(e) => handleLocate(e, { fromUserClick: true })}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation?.();
              }}
              className="locateBtn leaflet-top leaflet-right"
            >
              <img alt='Locate Me' className="locateMeImg" src={locateMe}></img>
            </Button>
          )}

          {visibleMapMarkers?.length > 0 &&
            <MarkerClusterGroup
              key={visibleMapMarkers
                .map(
                  (marker) =>
                    `${marker.id || marker.name}-${marker.addressOutputs?.[0]?.latitude}-${marker.addressOutputs?.[0]?.longitude}`
                )
                .join("|")}
              chunkedLoading
              showCoverageOnHover={false}
              maxClusterRadius={30}
              zoomToBoundsOnClick={true}
              spiderfyOnMaxZoom={false}
              eventHandlers={{
                clusterclick: (e) => {
                  const cluster = e.layer;
                  const markers = cluster.getAllChildMarkers();
                  if (!markers.length) return;
                  const first = markers[0].getLatLng();
                  const allSame = markers.every((m) => {
                    const pos = m.getLatLng();
                    return pos.lat === first.lat && pos.lng === first.lng;
                  });
                  if (allSame) {
                    e.originalEvent.preventDefault();
                    const list = markers.map((m) => m.options.data);
                    selectedOrg(
                      {
                        isCluster: true,
                        list,
                        latitude: first.lat,
                        longitude: first.lng,
                      },
                      true,
                      e.layer._map.latLngToContainerPoint(first)
                    );
                  }
                }
              }}
              iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();
                return L.divIcon({
                  html: `
                    <div class="custom-cluster">
                      ${count}
                    </div>
                  `,
                  className: "cluster-wrapper",
                  iconSize: L.point(40, 40),
                });
              }}
            >
              {visibleMapMarkers.map((data, index) => {
                const latitude = data.addressOutputs[0]?.latitude;
                const longitude = data.addressOutputs[0]?.longitude;
                if (!isValidLatLng(latitude, longitude)) return null;
                const markerKey = `${data.id || data.name}-${latitude}-${longitude}-${index}`;
                return (
                    <Marker
                      className="marker-pin"
                      key={markerKey}
                      icon={
                        new L.divIcon({
                          // previously companies were color coded wrt sectors
                          html: getMarkerByIndex(data.index, !data?.isActual),
                          iconAnchor: [25, 55],
                          popupAnchor: [4, -40],
                        })
                      }
                      position={[latitude, longitude]}
                      data={data}
                      eventHandlers={{
                        click: (e) => {
                          setHideToolTip("hideToolTip");
                          const businessCardPos =
                            e?.target._map.latLngToContainerPoint(e?.latlng);
                          selectedOrg(data, true, businessCardPos);
                          setOutsideClick(true);
                          setToolTipIndex(index);
                          updateCancelClick();
                        },
                        mouseover: (e) => {
                          const zoom = localStorage.getItem('zoom')
                          if (zoom >= 10) {
                            handleMouseOverAndOut(true);

                          }
                        },
                        mouseout: () => handleMouseOverAndOut(),
                      }}
                      ref={(el) => {
                        if (el) {
                          el.options.data = data;
                          markerRefs.current[markerKey] = el;
                        }
                      }}
                    >
                      {data?.isActual && <Tooltip
                        style={{ cursor: "pointer" }}
                        ref={(el) => {
                          if (el) tooltipRefs.current[index] = el;
                        }}
                        id={`leaflet-tooltip-${index}`}
                        key={`${data.addressOutputs[0]?.latitude}-${data.addressOutputs[0]?.longitude}`}
                        direction="top"
                        offset={[0, -50]}
                        data-parent-id={`${index}`}
                        className={`parent marker-tooltip-${index} marker-tooltip 
                      `}
                      >
                        <div
                          style={{ cursor: "pointer" }}
                          id="myElement"
                          className="tooltip-content"
                          ref={popupRef}
                          data-id={index}
                        >
                          <img
                            className="image-in-tooltip"
                            src={data.micrositeLogo}
                            alt={data.name}
                          />
                        </div>
                      </Tooltip>}
                    </Marker>
                );
              })}
            </MarkerClusterGroup>
          }
          {/* {mapMarkers?.length > 0 && allowSetView && (
            <SetViewOnMarker markers={mapMarkers} />
          )} */}
          <MapEventHandler />
        </MapContainer>
      ) : null}
      {activeMiniPopup?.id && markerOpenPopup()}
    </div>
  );
}